import React, { useEffect, useState } from 'react';
import { Button } from './ui/Button';
import { SearchIcon, MenuIcon, XIcon, ChevronDownIcon, PhoneIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate, Link } from 'react-router-dom';
export function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const [isScrolled, setIsScrolled] = useState(!isHomePage);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10 || !isHomePage);
    };
    // Initialize scroll state
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);
  const handleDropdownToggle = (dropdown: string) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };
  // Function to handle anchor links
  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, anchor: string) => {
    e.preventDefault();
    if (isHomePage) {
      // If already on home page, scroll to the section
      const element = document.getElementById(anchor);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    } else {
      // If not on home page, navigate to home page with the anchor
      navigate('/');
      // Need to wait for navigation to complete before scrolling
      setTimeout(() => {
        const element = document.getElementById(anchor);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }, 100);
    }
    // Close mobile menu if open
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };
  const navItems = [{
    name: 'Fonctionnalités',
    href: isHomePage ? '#features' : '/#features',
    anchor: 'features',
    dropdown: [{
      name: 'Alertes intelligentes',
      href: '/alertes-intelligentes'
    }, {
      name: 'Veille stratégique',
      href: '/veille-strategique'
    }, {
      name: 'Assistance Premium',
      href: '/assistance-premium'
    }],
    id: 'features-dropdown'
  }, {
    name: 'Démo',
    href: isHomePage ? '#demo' : '/#demo',
    anchor: 'demo',
    id: null
  }, {
    name: 'Tarifs',
    href: isHomePage ? '#pricing' : '/#pricing',
    anchor: 'pricing',
    id: null
  }, {
    name: 'À propos',
    href: isHomePage ? '#about' : '/#about',
    anchor: 'about',
    dropdown: [{
      name: 'Notre équipe',
      href: '/notre-equipe'
    }, {
      name: 'Partenaires',
      href: '/partenaires'
    }, {
      name: 'Blog',
      href: '/blog'
    }, {
      name: 'Contact',
      href: '/contact'
    }],
    id: 'about-dropdown'
  }];
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg py-3' : 'bg-blue-900/90 backdrop-blur-md py-4'}`} data-id="element-545">
      <div className="container mx-auto px-4" data-id="element-546">
        <div className="flex items-center justify-between" data-id="element-547">
          {/* Logo */}
          <Link to="/" className="flex items-center z-10" data-id="element-548">
            <div className="relative" data-id="element-549">
              <span className={`text-2xl font-bold ${isScrolled ? 'text-blue-900' : 'text-white'}`} data-id="element-550">
                Elevate<span className="text-yellow-500" data-id="element-551">IT</span>
              </span>
              <span className={`text-sm font-normal ml-0.5 ${isScrolled ? 'text-blue-600' : 'text-blue-200'}`} data-id="element-552">
                .fr
              </span>
              {/* Decorative underline */}
              <div className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-yellow-500 to-transparent" data-id="element-553"></div>
            </div>
          </Link>
          {/* Contact info - desktop only */}
          <div className={`hidden lg:flex items-center mr-auto ml-12 ${isScrolled ? 'text-blue-700' : 'text-blue-100'}`} data-id="element-554">
            <PhoneIcon size={16} className="mr-2" data-id="element-555" />
            <span className="text-sm" data-id="element-556">+33 7 53 29 34 19</span>
            <span className="mx-4 text-sm" data-id="element-557">|</span>
            <span className={`text-sm font-medium ${isScrolled ? 'text-blue-900' : 'text-white'}`} data-id="element-558">
              Lun-Ven: 9h-18h
            </span>
          </div>
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1 lg:space-x-2" data-id="element-559">
            {navItems.map(item => <div key={item.name} className="relative group" data-id="element-560">
                {item.dropdown ? <button onClick={() => handleDropdownToggle(item.id!)} className={`px-3 py-2 rounded-md text-sm font-medium flex items-center transition-colors ${isScrolled ? 'text-blue-900 hover:bg-blue-50' : 'text-white hover:bg-blue-800/60'} ${activeDropdown === item.id ? isScrolled ? 'bg-blue-50' : 'bg-blue-800/60' : ''}`} data-id="element-561">
                    {item.name}
                    <ChevronDownIcon size={16} className={`ml-1 transition-transform duration-200 ${activeDropdown === item.id ? 'rotate-180' : ''}`} data-id="element-562" />
                  </button> : <a href={item.href} className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${isScrolled ? 'text-blue-900 hover:bg-blue-50' : 'text-white hover:bg-blue-800/60'}`} onClick={e => item.anchor && handleAnchorClick(e, item.anchor)} data-id="element-563">
                    {item.name}
                  </a>}
                {/* Dropdown Menu */}
                {item.dropdown && <AnimatePresence data-id="element-564">
                    {activeDropdown === item.id && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute left-0 mt-1 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50" data-id="element-565">
                        <div className="py-1 rounded-md bg-white" data-id="element-566">
                          {item.dropdown.map(subItem => <Link key={subItem.name} to={subItem.href} className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-900" onClick={() => setActiveDropdown(null)} data-id="element-567">
                              {subItem.name}
                            </Link>)}
                        </div>
                      </motion.div>}
                  </AnimatePresence>}
              </div>)}
          </nav>
          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-3 ml-4" data-id="element-568">
            <Link to="/login" data-id="element-569">
              <Button variant="outline" size="sm" className={`border-2 px-4 ${isScrolled ? 'border-blue-900 text-blue-900' : 'border-white text-white hover:bg-blue-800/60'}`} data-id="element-570">
                Connexion
              </Button>
            </Link>
            <Link to="/essai-gratuit" data-id="element-571">
              <Button variant="primary" size="sm" className="shadow-md shadow-yellow-500/20 px-4 font-medium" data-id="element-572">
                Essai gratuit
              </Button>
            </Link>
          </div>
          {/* Mobile menu button */}
          <div className="md:hidden flex items-center" data-id="element-573">
            <button className={`p-2 rounded-md ${isScrolled ? 'text-blue-900 hover:bg-blue-50' : 'text-white hover:bg-blue-800/60'}`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu" data-id="element-574">
              {mobileMenuOpen ? <XIcon size={24} className="transition-transform duration-200 rotate-90" data-id="element-575" /> : <MenuIcon size={24} data-id="element-576" />}
            </button>
          </div>
        </div>
      </div>
      {/* Mobile menu */}
      <AnimatePresence data-id="element-577">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.3
      }} className="md:hidden overflow-hidden" data-id="element-578">
            <div className={`px-4 pt-2 pb-6 space-y-2 shadow-inner ${isScrolled ? 'bg-white' : 'bg-blue-900/95 backdrop-blur-md'}`} data-id="element-579">
              {navItems.map(item => <div key={item.name} className="py-1" data-id="element-580">
                  {item.dropdown ? <>
                      <button onClick={() => handleDropdownToggle(item.id!)} className={`w-full text-left px-3 py-3 rounded-md text-base font-medium flex items-center justify-between ${isScrolled ? 'text-blue-900 hover:bg-blue-50' : 'text-white hover:bg-blue-800/60'}`} data-id="element-581">
                        {item.name}
                        <ChevronDownIcon size={18} className={`transition-transform duration-200 ${activeDropdown === item.id ? 'rotate-180' : ''}`} data-id="element-582" />
                      </button>
                      <AnimatePresence data-id="element-583">
                        {activeDropdown === item.id && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.2
                }} className={`mt-1 ml-4 pl-4 border-l-2 ${isScrolled ? 'border-blue-100' : 'border-blue-700'}`} data-id="element-584">
                            {item.dropdown.map(subItem => <Link key={subItem.name} to={subItem.href} className={`block py-2.5 text-sm ${isScrolled ? 'text-blue-700 hover:text-blue-900' : 'text-blue-200 hover:text-white'}`} onClick={() => {
                    setActiveDropdown(null);
                    setMobileMenuOpen(false);
                  }} data-id="element-585">
                                {subItem.name}
                              </Link>)}
                          </motion.div>}
                      </AnimatePresence>
                    </> : <a href={item.href} className={`block px-3 py-3 rounded-md text-base font-medium ${isScrolled ? 'text-blue-900 hover:bg-blue-50' : 'text-white hover:bg-blue-800/60'}`} onClick={e => {
              if (item.anchor) {
                handleAnchorClick(e, item.anchor);
              } else {
                setMobileMenuOpen(false);
              }
            }} data-id="element-586">
                      {item.name}
                    </a>}
                </div>)}
              <div className="pt-4 space-y-3" data-id="element-587">
                <Link to="/login" className="w-full" data-id="element-588">
                  <Button variant={isScrolled ? 'outline' : 'secondary'} fullWidth className={isScrolled ? 'border-2 border-blue-900' : 'bg-blue-800/60 border border-blue-700'} data-id="element-589">
                    Connexion
                  </Button>
                </Link>
                <Link to="/essai-gratuit" className="w-full" data-id="element-590">
                  <Button variant="primary" fullWidth className="shadow-lg shadow-yellow-500/10 font-medium" data-id="element-591">
                    Essai gratuit 7 jours
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}